// Schedule.js – Create Class Schedule (Admin/Registry)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Button, CircularProgress, Alert, TextField, Typography, MenuItem,
  FormControl, InputLabel, Select
} from '@mui/material';
import { Autocomplete } from '@mui/material';

const api = axios.create({ baseURL: 'https://www.janalwilliamsedufoundation.online/' });
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const Schedule = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    grade_class_id: null,
    subject_id: null,
    teacher_id: null,
    day_of_week: '',
    period: '',
    start_time: '',
    end_time: ''
  });

  const [gradeClasses, setGradeClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ type: '', msg: '' });

  useEffect(() => {
    Promise.all([
      api.get('api/users/grade-classes/'),
      api.get('api/users/subjects/'),
      api.get('api/users/teacher-data/')
    ]).then(([gcRes, subRes, tRes]) => {
      setGradeClasses(gcRes.data);
      setSubjects(subRes.data);
      setTeachers(tRes.data);
    }).catch(err => {
      console.error("Failed to load dropdown data", err);
    });
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAlert({});

    const delay = new Promise(r => setTimeout(r, 3000));

    const payload = {
      grade_class_id: form.grade_class_id,
      subject_id: form.subject_id,
      teacher_id: form.teacher_id,
      day_of_week: Number(form.day_of_week),
      period: Number(form.period),
      start_time: form.start_time,
      end_time: form.end_time,
    };

    try {
      await Promise.all([api.post('class-schedules/', payload), delay]);
      setAlert({ type: 'success', msg: 'Schedule created successfully!' });
      setForm({
        grade_class_id: '',
        subject_id: '',
        teacher_id: '',
        day_of_week: '',
        period: '',
        start_time: '',
        end_time: ''
      });
      // navigate('/schedules');  <-- removed
    } catch (err) {
      const errorData = err?.response?.data;
      const msg = typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
      setAlert({ type: 'error', msg: msg || 'Failed to create schedule.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Typography variant="h5">Create Class Schedule</Typography>

      {alert.msg && (
        <Alert
          severity={alert.type}
          sx={{
            my: 2,
            bgcolor: alert.type === 'error' ? 'error.main' : 'info.light',
            color: '#fff'
          }}
        >
          {alert.msg}
        </Alert>
      )}

      <form onSubmit={handleSubmit} style={{ marginTop: '24px' }}>
        {/* Grade Class + Subject */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              options={gradeClasses}
              getOptionLabel={(option) => option.name}
              value={gradeClasses.find(gc => gc.id === form.grade_class_id) || null}
              onChange={(e, newValue) =>
                setForm({ ...form, grade_class_id: newValue ? newValue.id : null })
              }
              renderInput={(params) => (
                <TextField {...params} label="Grade Class" margin="normal" required fullWidth />
              )}
              PaperProps={{ style: { maxHeight: 200, overflowY: 'auto' } }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              options={subjects}
              getOptionLabel={(option) => option.name}
              value={subjects.find(s => s.id === form.subject_id) || null}
              onChange={(e, newValue) =>
                setForm({ ...form, subject_id: newValue ? newValue.id : null })
              }
              renderInput={(params) => (
                <TextField {...params} label="Subject" margin="normal" required fullWidth />
              )}
              PaperProps={{ style: { maxHeight: 200, overflowY: 'auto' } }}
            />
          </Box>
        </Box>

        {/* Teacher + Day of Week */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              options={teachers}
              getOptionLabel={(option) => option.full_name}
              value={teachers.find(t => t.id === form.teacher_id) || null}
              onChange={(e, newValue) =>
                setForm({ ...form, teacher_id: newValue ? newValue.id : null })
              }
              renderInput={(params) => (
                <TextField {...params} label="Teacher" margin="normal" required fullWidth />
              )}
              PaperProps={{ style: { maxHeight: 200, overflowY: 'auto' } }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Day of Week</InputLabel>
              <Select
                name="day_of_week"
                value={form.day_of_week}
                onChange={handleChange}
                required
              >
                <MenuItem value={1}>Monday</MenuItem>
                <MenuItem value={2}>Tuesday</MenuItem>
                <MenuItem value={3}>Wednesday</MenuItem>
                <MenuItem value={4}>Thursday</MenuItem>
                <MenuItem value={5}>Friday</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Period + Start Time */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Period</InputLabel>
              <Select
                name="period"
                value={form.period}
                onChange={handleChange}
                required
              >
                <MenuItem value={1}>1st Period</MenuItem>
                <MenuItem value={2}>2nd Period</MenuItem>
                <MenuItem value={3}>3rd Period</MenuItem>
                <MenuItem value={99}>Recess Period</MenuItem>
                <MenuItem value={4}>4th Period</MenuItem>
                <MenuItem value={5}>5th Period</MenuItem>
                <MenuItem value={6}>6th Period</MenuItem>
                <MenuItem value={7}>7th Period</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              name="start_time"
              label="Start Time"
              type="time"
              value={form.start_time}
              onChange={handleChange}
              required
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        {/* End Time - Full width at bottom */}
        <TextField
          fullWidth
          name="end_time"
          label="End Time"
          type="time"
          value={form.end_time}
          onChange={handleChange}
          required
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />

        <Box sx={{ mt: 3, position: 'relative' }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
          >
            Submit
          </Button>
          {loading && (
            <CircularProgress
              size={24}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                mt: '-12px',
                ml: '-12px'
              }}
            />
          )}
        </Box>
      </form>
    </Box>
  );
};

export default Schedule;



// // Schedule.js – Create Class Schedule (Admin/Registry)
// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import {
//   Box, Button, CircularProgress, Alert, TextField, Typography, MenuItem,
//   FormControl, InputLabel, Select
// } from '@mui/material';
// import { Autocomplete } from '@mui/material';

// const api = axios.create({ baseURL: 'http://127.0.0.1:8000/' });
// api.interceptors.request.use(config => {
//   const token = localStorage.getItem('access');
//   if (token) config.headers.Authorization = `Bearer ${token}`;
//   return config;
// });

// const Schedule = () => {
//   const navigate = useNavigate();
//   const [form, setForm] = useState({
//     grade_class_id: null,
//     subject_id: null,
//     teacher_id: null,
//     day_of_week: '',
//     period: '',
//     start_time: '',
//     end_time: ''
//   });

//   const [gradeClasses, setGradeClasses] = useState([]);
//   const [subjects, setSubjects] = useState([]);
//   const [teachers, setTeachers] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [alert, setAlert] = useState({ type: '', msg: '' });

//   useEffect(() => {
//     Promise.all([
//       api.get('api/users/grade-classes/'),
//       api.get('api/users/subjects/'),
//       api.get('api/users/teacher-data/')
//     ]).then(([gcRes, subRes, tRes]) => {
//       setGradeClasses(gcRes.data);
//       setSubjects(subRes.data);
//       setTeachers(tRes.data);
//     }).catch(err => {
//       console.error("Failed to load dropdown data", err);
//     });
//   }, []);

//   const handleChange = (e) => {
//     setForm({ ...form, [e.target.name]: e.target.value });
//   };

// const handleSubmit = async (e) => {
//   e.preventDefault();
//   setLoading(true);
//   setAlert({});

//   const delay = new Promise(r => setTimeout(r, 3000));

//   const payload = {
//     grade_class_id: form.grade_class_id,
//     subject_id: form.subject_id,
//     teacher_id: form.teacher_id,
//     day_of_week: Number(form.day_of_week),
//     period: Number(form.period),
//     start_time: form.start_time,
//     end_time: form.end_time,
//   };

//   try {
//     await Promise.all([api.post('class-schedules/', payload), delay]);
//     setAlert({ type: 'success', msg: 'Schedule created successfully!' });
//     setForm({
//       grade_class_id: '',
//       subject_id: '',
//       teacher_id: '',
//       day_of_week: '',
//       period: '',
//       start_time: '',
//       end_time: ''
//     });
//     // navigate('/schedules');  <-- removed
//   } catch (err) {
//     const errorData = err?.response?.data;
//     const msg = typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
//     setAlert({ type: 'error', msg: msg || 'Failed to create schedule.' });
//   } finally {
//     setLoading(false);
//   }
// };


//   return (
//     <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
//       <Typography variant="h5">Create Class Schedule</Typography>

//       {alert.msg && (
//         <Alert
//           severity={alert.type}
//           sx={{
//             my: 2,
//             bgcolor: alert.type === 'error' ? 'error.main' : 'info.light',
//             color: '#fff'
//           }}
//         >
//           {alert.msg}
//         </Alert>
//       )}

//       <form onSubmit={handleSubmit}>
//         {/* Grade Class Autocomplete */}
//         <Autocomplete
//           options={gradeClasses}
//           getOptionLabel={(option) => option.name}
//           value={gradeClasses.find(gc => gc.id === form.grade_class_id) || null}
//           onChange={(e, newValue) =>
//             setForm({ ...form, grade_class_id: newValue ? newValue.id : null })
//           }
//           renderInput={(params) => (
//             <TextField {...params} label="Grade Class" margin="normal" required />
//           )}
//           PaperProps={{ style: { maxHeight: 200, overflowY: 'auto' } }}
//         />

//         {/* Subject Autocomplete */}
//         <Autocomplete
//           options={subjects}
//           getOptionLabel={(option) => option.name}
//           value={subjects.find(s => s.id === form.subject_id) || null}
//           onChange={(e, newValue) =>
//             setForm({ ...form, subject_id: newValue ? newValue.id : null })
//           }
//           renderInput={(params) => (
//             <TextField {...params} label="Subject" margin="normal" required />
//           )}
//           PaperProps={{ style: { maxHeight: 200, overflowY: 'auto' } }}
//         />

//         {/* Teacher Autocomplete */}
//         <Autocomplete
//           options={teachers}
//           getOptionLabel={(option) => option.full_name}
//           value={teachers.find(t => t.id === form.teacher_id) || null}
//           onChange={(e, newValue) =>
//             setForm({ ...form, teacher_id: newValue ? newValue.id : null })
//           }
//           renderInput={(params) => (
//             <TextField {...params} label="Teacher" margin="normal" required />
//           )}
//           PaperProps={{ style: { maxHeight: 200, overflowY: 'auto' } }}
//         />

//         {/* Day of Week */}
//         <FormControl fullWidth margin="normal">
//           <InputLabel>Day of Week</InputLabel>
//           <Select
//             name="day_of_week"
//             value={form.day_of_week}
//             onChange={handleChange}
//             required
//           >
//             <MenuItem value={1}>Monday</MenuItem>
//             <MenuItem value={2}>Tuesday</MenuItem>
//             <MenuItem value={3}>Wednesday</MenuItem>
//             <MenuItem value={4}>Thursday</MenuItem>
//             <MenuItem value={5}>Friday</MenuItem>
//           </Select>
//         </FormControl>

//         {/* Period */}
//         <FormControl fullWidth margin="normal">
//           <InputLabel>Period</InputLabel>
//           <Select
//             name="period"
//             value={form.period}
//             onChange={handleChange}
//             required
//           >
//             <MenuItem value={1}>1st Period</MenuItem>
//             <MenuItem value={2}>2nd Period</MenuItem>
//             <MenuItem value={3}>3rd Period</MenuItem>
//             <MenuItem value={99}>Recess Period</MenuItem>
//             <MenuItem value={4}>4th Period</MenuItem>
//             <MenuItem value={5}>5th Period</MenuItem>
//             <MenuItem value={6}>6th Period</MenuItem>
//             <MenuItem value={7}>7th Period</MenuItem>
//           </Select>
//         </FormControl>

//         {/* Start Time */}
//         <TextField
//           fullWidth
//           name="start_time"
//           label="Start Time"
//           type="time"
//           value={form.start_time}
//           onChange={handleChange}
//           required
//           margin="normal"
//           InputLabelProps={{ shrink: true }}
//         />

//         {/* End Time */}
//         <TextField
//           fullWidth
//           name="end_time"
//           label="End Time"
//           type="time"
//           value={form.end_time}
//           onChange={handleChange}
//           required
//           margin="normal"
//           InputLabelProps={{ shrink: true }}
//         />

//         <Box sx={{ mt: 3, position: 'relative' }}>
//           <Button
//             type="submit"
//             fullWidth
//             variant="contained"
//             disabled={loading}
//           >
//             Submit
//           </Button>
//           {loading && (
//             <CircularProgress
//               size={24}
//               sx={{
//                 position: 'absolute',
//                 top: '50%',
//                 left: '50%',
//                 mt: '-12px',
//                 ml: '-12px'
//               }}
//             />
//           )}
//         </Box>
//       </form>
//     </Box>
//   );
// };

// export default Schedule;
